import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { FileUploader } from 'ng2-file-upload';
import { OkAloneService } from 'src/app/ok-alone.service';

@Component({
  selector: 'app-devices',
  templateUrl: './devices.component.html',
  styleUrls: ['./devices.component.scss']
})
export class DevicesComponent implements OnInit {
  devices: any[] = [];
  uploader!: FileUploader;
  hasBaseDropZoneOver = false;
  uploadStep = 1;
  uploadError = '';
  uploadRows: any[] = [];
  uploadSummary: any = null;
  importSummary: any = null;
  showForm = false;
  isSaving = false;
  editingId: number | null = null;

  filterForm = new FormGroup({
    search: new FormControl('', []),
    sort: new FormControl('newest', []),
  });

  deviceForm = new FormGroup({
    name: new FormControl('', [Validators.minLength(3)]),
    type: new FormControl('zoleo', [Validators.required]),
    imei: new FormControl('', [Validators.required, Validators.pattern(/^\d{15}$/)]),
    number: new FormControl('', [Validators.required]),
  });

  pagination = { page: 1, pageSize: 1, itemCount: 1, totalPages: 1, pageArray: [1], totalCount: 1 };

  constructor(public okService: OkAloneService, private transloco: TranslocoService) {
    this.setupUploader();
  }

  ngOnInit(): void {
    this.pagination.page = 1;
    this.getDevices();
  }

  setupUploader(): void {
    const url = this.okService.getUrl('devices/upload');
    const token = 'Bearer ' + this.okService.session.token;

    this.uploader = new FileUploader({
      url: url,
      authToken: token,
      authTokenHeader: 'Authorization',
      itemAlias: 'upload',
      disableMultipart: false,
    });

    this.uploader.onAfterAddingFile = (item) => {
      item.withCredentials = false;
    };

    this.uploader.response.subscribe((res) => {
      this.uploadError = '';

      let data: any = null;
      try {
        data = JSON.parse(res);
      } catch (e) {
        this.uploadError = this.transloco.translate('There is a problem with the file. Please check the file type and try again.');
        return;
      }

      if (data?.error) {
        this.uploadError = data.error;
        return;
      }

      this.uploadRows = Array.isArray(data?.rows) ? data.rows : [];
      this.uploadSummary = data?.summary ?? this.getUploadSummary(this.uploadRows);
      this.importSummary = null;

      if (this.uploadRows.length === 0) {
        this.uploadError = this.transloco.translate('No device rows found in the uploaded file.');
        return;
      }

      this.uploadStep = 2;
    });
  }

  openUploadModal(): void {
    this.resetUpload();
  }

  resetUpload(): void {
    this.uploadStep = 1;
    this.uploadError = '';
    this.uploadRows = [];
    this.uploadSummary = null;
    this.importSummary = null;
    this.setupUploader();
  }

  importDevices(): void {
    this.uploadError = '';
    this.okService.post('devices/upload', { devices: this.uploadRows }).subscribe({
      next: (response) => {
        this.importSummary = response.body;
        this.uploadStep = 3;
        this.getDevices();
        this.okService.setAlert(this.transloco.translate('The device upload has been completed'), 'success');
      },
      error: (error) => {
        this.uploadError = error?.error?.error || this.transloco.translate('The upload could not be completed.');
      },
    });
  }

  public fileOverBase(e: any): void {
    this.hasBaseDropZoneOver = e;
  }

  get canImport(): boolean {
    return this.uploadRows.some((row) => row.status === 'new');
  }

  getUploadSummary(rows: any[]): any {
    return {
      new: rows.filter((row) => row.status === 'new').length,
      already_present: rows.filter((row) => row.status === 'already_present').length,
      invalid: rows.filter((row) => row.status === 'invalid').length,
      duplicate: rows.filter((row) => row.status === 'duplicate').length,
    };
  }

  statusBadge(status: string): string {
    if (status === 'new') return 'bg-primary';
    if (status === 'already_present') return 'bg-secondary';
    if (status === 'duplicate') return 'bg-warning';
    if (status === 'invalid') return 'bg-danger';
    if (status === 'imported') return 'bg-success';
    return 'bg-light text-dark';
  }

  statusLabel(status: string): string {
    if (status === 'new') return 'New';
    if (status === 'already_present') return 'Already Present';
    if (status === 'duplicate') return 'Duplicate';
    if (status === 'invalid') return 'Invalid';
    if (status === 'imported') return 'Imported';
    return status;
  }

  getDevices(): void {
    const filter = {
      sort: this.filterForm.value.sort ?? 'newest',
      search: this.filterForm.value.search ?? '',
      page: this.pagination.page,
    };

    this.okService.get('devices', filter).subscribe((data) => {
      this.devices = (data.body as any[]) ?? [];
      this.pagination = this.okService.setPagination(data.headers) ?? this.pagination;
    });
  }

  updateFilter(resetPage = true): void {
    if (resetPage) {
      this.pagination.page = 1;
    }
    this.getDevices();
  }

  clearFilter(): void {
    this.filterForm.patchValue({ search: '', sort: 'newest' });
    this.updateFilter(true);
  }

  exportDevices(): void {
    this.okService.download('devices/export');
  }

  openAddForm(): void {
    this.showForm = true;
    this.editingId = null;
    this.deviceForm.reset({
      name: '',
      type: 'zoleo',
      imei: '',
      number: '',
    });
    this.deviceForm.markAsPristine();
    this.deviceForm.markAsUntouched();
  }

  openEditForm(device: any): void {
    this.showForm = true;
    this.editingId = device.id;
    this.deviceForm.reset({
      name: device.name ?? '',
      type: device.type ?? 'zoleo',
      imei: device.imei ?? '',
      number: device.number ?? '',
    });
    this.deviceForm.markAsPristine();
    this.deviceForm.markAsUntouched();
  }

  cancelForm(): void {
    this.showForm = false;
    this.editingId = null;
    this.isSaving = false;
  }

  saveDevice(): void {
    this.deviceForm.markAllAsTouched();

    if (!this.deviceForm.valid) {
      return;
    }

    const payload = {
      name: this.cleanName(this.deviceForm.value.name),
      type: 'zoleo',
      imei: String(this.deviceForm.value.imei ?? ''),
      number: String(this.deviceForm.value.number ?? ''),
    };

    this.isSaving = true;

    if (this.editingId != null) {
      this.okService.put(`devices/${this.editingId}`, payload).subscribe({
        next: () => {
          this.okService.setAlert(this.transloco.translate('The device has been updated'), 'success');
          this.cancelForm();
          this.getDevices();
        },
        error: () => {
          this.isSaving = false;
        },
      });
      return;
    }

    this.okService.post('devices', payload).subscribe({
      next: () => {
        this.okService.setAlert(this.transloco.translate('The device has been added'), 'success');
        this.cancelForm();
        this.updateFilter(true);
      },
      error: () => {
        this.isSaving = false;
      },
    });
  }

  deleteDevice(device: any): void {
    if (!window.confirm(this.transloco.translate('Are you sure you want to delete this device?'))) {
      return;
    }

    this.okService.delete(`devices/${device.id}`, {}).subscribe(() => {
      this.okService.setAlert(this.transloco.translate('The device has been deleted'), 'success');
      this.getDevices();
    });
  }

  private cleanName(name: string | null | undefined): string {
    const value = String(name ?? '').trim();

    return value === '' ? '' : value;
  }
}
